import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import { get } from "lodash-es";
import React, { FC, useState } from "react";

import { Card, InputAdornment, Stack } from "@mui/material";
import Typography from "@mui/material/Typography";

import SaveButton from "../../../components/SaveButton";
import { ConfirmationDialog } from "../../../components/confirmation-dialog";
import { ViewIcon } from "../../../components/icons";
import TextField from "../../../components/styled/TextField";
import { firebaseAuth } from "../../../configs/firebase";
import { logout } from "../../../slices/current";
import { pushError, pushSuccess } from "../../../slices/notifications";
import { useDispatch } from "../../../store";
import { useDeleteCurrentUserMutation } from "../../../store/pomeriumApi";
import { parseErrorCode } from "../../../utils/firebaseErrorParser";

const General: FC = () => {
  const dispatch = useDispatch();
  const [deleteUser] = useDeleteCurrentUserMutation();
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [oldPassword, setOldPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [showOldPassword, setShowOldPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const deleteAccount = async () => {
    const res = await deleteUser();
    if (!get(res, "error", false)) {
      dispatch(logout());
    } else {
      setConfirmDelete(false);
    }
  };

  const oneProvider =
    (firebaseAuth.currentUser?.providerData || []).length === 1;
  const isEmailPasswordUser =
    oneProvider &&
    get(firebaseAuth, "currentUser.providerData.0.providerId") === "password";

  const updateUserPassword = async () => {
    try {
      if (firebaseAuth.currentUser) {
        const credential = EmailAuthProvider.credential(
          firebaseAuth.currentUser?.email || "",
          oldPassword,
        );
        await reauthenticateWithCredential(
          firebaseAuth.currentUser,
          credential,
        );
        await updatePassword(firebaseAuth.currentUser, newPassword);
        dispatch(pushSuccess("Your password has been updated."));
      }
    } catch (e) {
      dispatch(pushError(parseErrorCode(e)));
    }
  };

  return (
    <Stack gap={4}>
      <ConfirmationDialog
        message={
          "Are you sure you wish to delete your account and all of the associated clusters? This can't be undone!"
        }
        open={confirmDelete}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={deleteAccount}
        title="Permanently Delete Account?"
        variant="warning"
      />

      {isEmailPasswordUser && (
        <Card
          sx={{
            p: 2,
          }}
        >
          <Stack direction="row" gap={4}>
            <Stack sx={{ width: "18vw" }}>
              <Typography variant="h5" fontWeight={600}>
                Change password
              </Typography>
            </Stack>
            <Stack gap={3}>
              <Stack>
                <Typography variant="subtitle2">Old password</Typography>
                <TextField
                  value={oldPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setOldPassword(e.target.value)
                  }
                  type={showOldPassword ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <ViewIcon
                          onClick={() => setShowOldPassword(!showOldPassword)}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
              <Stack>
                <Typography variant="subtitle2">New password</Typography>
                <TextField
                  value={newPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setNewPassword(e.target.value)
                  }
                  type={showNewPassword ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <ViewIcon
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
              <SaveButton
                variant="contained"
                onClick={updateUserPassword}
                disabled={!oldPassword || !newPassword}
              >
                Save settings
              </SaveButton>
            </Stack>
          </Stack>
        </Card>
      )}
      <Card
        sx={{
          p: 2,
        }}
      >
        <Stack direction="row" gap={4}>
          <Stack sx={{ width: "18vw", maxWidth: "18vw" }}>
            <Typography variant="h5" fontWeight={600}>
              Delete my account
            </Typography>
            <Typography variant="body2" fontWeight={400} color="text.secondary">
              Permanently delete this account and associated clusters. This
              action cannot be undone.
            </Typography>
          </Stack>
          <Stack>
            <SaveButton
              variant="contained"
              color="error"
              onClick={() => setConfirmDelete(true)}
            >
              Delete my account
            </SaveButton>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
};
export default General;
